import {Avatar as AvatarPrimitive} from '@base-ui/react/avatar';
import {Children, isValidElement} from 'react';

import {cn} from '../../lib/utils';
import {AVATAR_STACK_MAX_SLOTS, getAvatarStack} from '../Avatar/avatar-stack';
import {avatarShapeClasses, avatarSizeClasses, avatarSizeStyle} from '../Avatar/avatar-variants';
import {CenteredText} from '../Avatar/CenteredText';

import type {AvatarShape, AvatarSize} from '../Avatar/avatar-variants';
import type React from 'react';
import type {ComponentProps, ReactNode} from 'react';

function Avatar({
  className,
  size = 'default',
  shape = 'circle',
  style,
  ...props
}: AvatarPrimitive.Root.Props & {size?: AvatarSize; shape?: AvatarShape}) {
  return (
    <AvatarPrimitive.Root
      data-slot="avatar"
      data-size={size}
      data-shape={shape}
      style={{...avatarSizeStyle(size), ...style}}
      className={cn(
        'group/avatar relative flex shrink-0 overflow-hidden rounded-full border border-border shadow-sm select-none',
        avatarSizeClasses,
        avatarShapeClasses,
        className
      )}
      {...props}
    />
  );
}

function AvatarImage({className, ...props}: AvatarPrimitive.Image.Props) {
  return (
    <AvatarPrimitive.Image
      data-slot="avatar-image"
      className={cn(
        'aspect-square size-full rounded-full object-cover group-data-[shape=square]/avatar:rounded-md group-data-[size=xs]/avatar:group-data-[shape=square]/avatar:rounded-(--avatar-xs-square-radius)',
        className
      )}
      {...props}
    />
  );
}

function AvatarFallback({
  className,
  colorIndex,
  style,
  children,
  ...props
}: AvatarPrimitive.Fallback.Props & {colorIndex?: number}) {
  const colorStyle =
    colorIndex !== undefined
      ? ({
          '--av-bg': `var(--avatar-${colorIndex}-bg)`,
          '--av-fg': `var(--avatar-${colorIndex}-fg)`,
          ...style
        } as unknown as React.CSSProperties)
      : style;

  return (
    <AvatarPrimitive.Fallback
      data-slot="avatar-fallback"
      style={colorStyle}
      className={cn(
        'flex size-full items-center justify-center rounded-full font-semibold',
        colorIndex !== undefined
          ? 'bg-(--av-bg) text-(--av-fg)'
          : 'bg-background-selected-strong text-brand-accent',
        'text-(length:--avatar-font)',
        'group-data-[shape=square]/avatar:rounded-sm group-data-[size=xs]/avatar:group-data-[shape=square]/avatar:rounded-(--avatar-xs-square-radius)',
        className
      )}
      {...props}
    >
      {typeof children === 'string' ? <CenteredText>{children}</CenteredText> : children}
    </AvatarPrimitive.Fallback>
  );
}

function AvatarBadge({className, ...props}: ComponentProps<'span'>) {
  return (
    <span
      data-slot="avatar-badge"
      className={cn(
        'absolute end-0 bottom-0 z-10 inline-flex items-center justify-center rounded-full bg-brand-primary text-fg_on-brand bg-blend-color ring-2 ring-background select-none',
        'group-data-[size=xs]/avatar:hidden group-data-[size=compact]/avatar:hidden',
        'group-data-[size=sm]/avatar:size-2 group-data-[size=sm]/avatar:[&>svg]:hidden',
        'group-data-[size=default]/avatar:size-2.5 group-data-[size=default]/avatar:[&>svg]:size-2',
        'group-data-[size=lg]/avatar:size-3 group-data-[size=lg]/avatar:[&>svg]:size-2',
        'group-data-[size=xl]/avatar:size-3.5 group-data-[size=xl]/avatar:[&>svg]:size-2.5',
        className
      )}
      {...props}
    />
  );
}

function getChildAvatarShape(child: ReactNode): AvatarShape | undefined {
  if (!isValidElement(child)) return undefined;
  return (child.props as {shape?: AvatarShape}).shape;
}

function AvatarGroup({
  className,
  size = 'default',
  max = AVATAR_STACK_MAX_SLOTS,
  children,
  style,
  ...props
}: ComponentProps<'div'> & {size?: AvatarSize; max?: number}) {
  const {visible, overflow} = getAvatarStack(Children.toArray(children), max);
  // Undefined here falls through to AvatarGroupCount's own 'circle' default.
  const lastAvatarShape = getChildAvatarShape(visible[visible.length - 1]);

  return (
    <div
      data-slot="avatar-group"
      data-size={size}
      style={{...avatarSizeStyle(size), ...style}}
      className={cn(
        'group/avatar-group flex items-center',
        // Consumers on a non-surface background override the ring with `[--avatar-ring:var(--background-secondary)]`.
        '[--avatar-ring:var(--background-surface)]',
        className
      )}
      {...props}
    >
      <div
        data-slot="avatar-group-stack"
        className={cn(
          'flex',
          // Overlap is 25% of the avatar width (see `avatarSizes`) so the stack reads the same at every size.
          '-space-x-(--avatar-overlap)',
          // A 2px ring (not a border) separates overlapping avatars.
          '*:border-0 *:shadow-none *:ring-2 *:ring-(--avatar-ring)',
          // Descending z keeps the leading avatar on top — correct in both directions.
          '[&>*:nth-child(1)]:z-50 [&>*:nth-child(2)]:z-40 [&>*:nth-child(3)]:z-30 [&>*:nth-child(4)]:z-20 [&>*:nth-child(5)]:z-10'
        )}
      >
        {visible}
      </div>
      {overflow > 0 && <AvatarGroupCount count={overflow} shape={lastAvatarShape} />}
    </div>
  );
}

function AvatarGroupCount({
  className,
  count,
  shape = 'circle',
  ...props
}: Omit<ComponentProps<'div'>, 'children'> & {count: number; shape?: AvatarShape}) {
  return (
    <div
      data-slot="avatar-group-count"
      data-shape={shape}
      dir="ltr"
      className={cn(
        'relative flex shrink-0 items-center justify-center rounded-full border border-border bg-background-tertiary font-medium text-fg-secondary',
        'size-(--avatar-size) text-(length:--avatar-chip-font)',
        'data-[shape=square]:rounded-md group-data-[size=xs]/avatar-group:data-[shape=square]:rounded-(--avatar-xs-square-radius)',
        className
      )}
      {...props}
    >
      <CenteredText>{`+${count}`}</CenteredText>
    </div>
  );
}

export {Avatar, AvatarImage, AvatarFallback, AvatarGroup, AvatarGroupCount, AvatarBadge};
